import React, { useCallback, useState, useRef, useEffect } from 'react';
import type { ApiService } from '@pro-groups/shared';
import {
  sendSystemVerificationCode,
  loginWithCode,
} from '../services/WebAuthService';
import type { SignInResult } from './WebSignInView';

type Step = 'credentials' | 'verify';

interface Props {
  apiService: ApiService;
  accessType: '20' | '25';
  title: string;
  subtitle: string;
  policyNotice?: string;
  onSignIn: (result: SignInResult) => void;
  onBackToPro?: () => void;
}

export const WebAdminSignInView: React.FC<Props> = ({
  apiService,
  accessType,
  title,
  subtitle,
  policyNotice,
  onSignIn,
  onBackToPro,
}) => {
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState(() => {
    try {
      return window.localStorage.getItem('user_name') ?? '';
    } catch {
      return '';
    }
  });
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const isSystemMode = accessType === '25';

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const clearMessages = () => { setError(''); setInfo(''); };

  const handleSendVerificationCode = useCallback(async () => {
    clearMessages();

    if (!email.trim() || !userName.trim() || !password.trim()) {
      setError('Email, name, and password are required.');
      return;
    }

    setLoading(true);
    const result = await sendSystemVerificationCode(
      email.trim().toLowerCase(),
      userName.trim(),
      password,
      apiService,
      { accessType },
    );
    setLoading(false);

    if (result.success && result.verificationToken) {
      setVerificationToken(result.verificationToken);
      setStep('verify');
      setInfo('Verification code sent to your email.');
      setResendCooldown(60);
    } else {
      setError(result.message ?? 'Failed to send verification code.');
    }
  }, [email, userName, password, apiService, accessType]);

  const handleVerifyCode = useCallback(async () => {
    clearMessages();

    if (code.trim().length !== 6) {
      setError('Please enter the 6-digit verification code.');
      return;
    }

    setLoading(true);
    const result = await loginWithCode(
      email.trim().toLowerCase(),
      userName.trim(),
      code.trim(),
      verificationToken,
      apiService,
      { accessType },
    );
    setLoading(false);

    if (result.success && result.data) {
      onSignIn(result.data as SignInResult);
    } else {
      setError(result.message ?? 'Verification failed. Please try again.');
    }
  }, [email, userName, code, verificationToken, apiService, accessType, onSignIn]);

  const handleResendCode = useCallback(async () => {
    if (resendCooldown > 0) return;
    
    clearMessages();
    setLoading(true);
    const result = await sendSystemVerificationCode(
      email.trim().toLowerCase(),
      userName.trim(),
      password,
      apiService,
      { accessType },
    );
    setLoading(false);

    if (result.success && result.verificationToken) {
      setVerificationToken(result.verificationToken);
      setInfo('Verification code resent to your email.');
      setResendCooldown(60);
    } else {
      setError(result.message ?? 'Failed to resend verification code.');
    }
  }, [email, userName, password, apiService, accessType, resendCooldown]);

  const handleBackToCredentials = useCallback(() => {
    setStep('credentials');
    setCode('');
    setVerificationToken('');
    clearMessages();
  }, []);

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 20, background: '#eef2f7' }}>
      <div style={{ width: '100%', maxWidth: 460, background: '#fff', borderRadius: 16, padding: '32px 28px', boxShadow: '0 6px 24px rgba(0,0,0,0.1)' }}>
        <h1 style={{ margin: 0, fontSize: 24, color: '#1f2937' }}>
          {isSystemMode ? 'Sign In' : title}
        </h1>
        {!isSystemMode && step === 'credentials' && (
          <p style={{ marginTop: 8, color: '#6b7280', fontSize: 14 }}>{subtitle}</p>
        )}

        {policyNotice && step === 'credentials' && (
          <div style={{ marginTop: 12, marginBottom: 12, fontSize: 12, color: '#7c2d12', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8, padding: '10px 12px' }}>
            {policyNotice}
          </div>
        )}

        {error && (
          <div style={{ marginTop: 10, marginBottom: 12, fontSize: 13, color: '#991b1b', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 10px' }}>
            {error}
          </div>
        )}

        {info && (
          <div style={{ marginTop: 10, marginBottom: 12, fontSize: 13, color: '#065f46', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 8, padding: '8px 10px' }}>
            {info}
          </div>
        )}

        {step === 'credentials' && (
          <>
            <Input label="Email" type="email" value={email} onChange={setEmail} />
            <Input label="Name" type="text" value={userName} onChange={setUserName} />
            <Input label="Password" type="password" value={password} onChange={setPassword} />

            <button 
              type="button" 
              onClick={handleSendVerificationCode} 
              disabled={loading} 
              style={primaryButtonStyle}
            >
              {loading ? 'Sending...' : 'Send Verification Code'}
            </button>

            {!isSystemMode && onBackToPro && (
              <button type="button" onClick={onBackToPro} disabled={loading} style={{ ...secondaryButtonStyle, marginTop: 14 }}>
                Use Pro Sign-In
              </button>
            )}
          </>
        )}

        {step === 'verify' && (
          <>
            <p style={{ marginTop: 16, marginBottom: 16, color: '#6b7280', fontSize: 14, textAlign: 'center' }}>
              Enter the 6-digit code sent to <strong>{email}</strong>
            </p>

            <CodeInput value={code} onChange={setCode} disabled={loading} />

            <button 
              type="button" 
              onClick={handleVerifyCode} 
              disabled={loading || code.trim().length !== 6} 
              style={primaryButtonStyle}
            >
              {loading ? 'Verifying...' : 'Verify and Sign In'}
            </button>

            <button 
              type="button" 
              onClick={handleResendCode} 
              disabled={loading || resendCooldown > 0} 
              style={{ ...secondaryButtonStyle, marginTop: 14 }}
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
            </button>

            <button 
              type="button" 
              onClick={handleBackToCredentials} 
              disabled={loading} 
              style={{ ...secondaryButtonStyle, marginTop: 8 }}
            >
              Back to Credentials
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const CODE_LENGTH = 6;

const CodeInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const digits = value.padEnd(CODE_LENGTH, ' ').split('').slice(0, CODE_LENGTH);

  const handleKey = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const arr = [...digits];
      if (arr[idx].trim() === '' && idx > 0) {
        arr[idx - 1] = ' ';
        onChange(arr.join('').replace(/ +$/, ''));
        refs.current[idx - 1]?.focus();
      } else {
        arr[idx] = ' ';
        onChange(arr.join('').replace(/ +$/, ''));
      }
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      refs.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowRight' && idx < CODE_LENGTH - 1) {
      refs.current[idx + 1]?.focus();
    }
  };

  const handleChange = (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    if (!char) return;
    const arr = [...digits];
    arr[idx] = char;
    onChange(arr.join('').replace(/ +$/, ''));
    if (idx < CODE_LENGTH - 1) refs.current[idx + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    onChange(pasted);
    const focusIdx = Math.min(pasted.length, CODE_LENGTH - 1);
    refs.current[focusIdx]?.focus();
  };

  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 16 }}>
      {Array.from({ length: CODE_LENGTH }, (_, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digits[i].trim()}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          disabled={disabled}
          aria-label={`Digit ${i + 1}`}
          style={{
            width: 48,
            height: 56,
            fontSize: 22,
            fontWeight: 700,
            textAlign: 'center',
            border: `2px solid ${digits[i].trim() ? '#2563eb' : '#d1d5db'}`,
            borderRadius: 8,
            outline: 'none',
            background: '#fff',
            color: '#1f2937',
            caretColor: 'transparent',
            transition: 'border-color 0.15s',
            fontFamily: 'monospace',
          }}
        />
      ))}
    </div>
  );
};

interface InputProps {
  label: string;
  type: string;
  value: string;
  onChange: (value: string) => void;
}

const Input: React.FC<InputProps> = ({ label, type, value, onChange }) => {
  return (
    <label style={{ display: 'block', marginTop: 10 }}>
      <span style={{ display: 'block', marginBottom: 6, fontSize: 12, color: '#6b7280' }}>{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 12px', fontSize: 14 }}
      />
    </label>
  );
};

const primaryButtonStyle: React.CSSProperties = {
  width: '100%',
  border: 'none',
  borderRadius: 8,
  padding: '10px 14px',
  marginTop: 12,
  background: '#2563eb',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryButtonStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  padding: '10px 14px',
  background: '#fff',
  color: '#374151',
  fontWeight: 600,
  cursor: 'pointer',
};
